// frontend/src/tenant/tenantUi.ts
//
// The frontend half of the Tenant Pack: terminology (lexicon), extra field
// descriptors surfacing tenant-attached `ext.*` data, per-tenant default
// views, and hidden Core fields. Set once by createApp({ tenant }) before
// mount; consumed by Core modules through the accessors below. Core never
// imports tenant code — the pack is plain data (plus optional components
// registered elsewhere).
//
// Defaults reproduce today's wording exactly: an app with no pack (Core
// standalone) or an empty pack renders byte-for-byte as before.

export interface LexTerm { singular: string; plural: string; long?: string; short?: string }
export type Lexicon = Record<string, LexTerm>;

export interface TenantFieldDescriptor {
  /** Registry key — must not collide with Core field keys. */
  key: string;
  /** Dot path into the entity doc (typically 'ext.…'). */
  source: string;
  label: string;
  /** Which list the field belongs to. */
  entity: 'job' | 'part' | 'op' | 'salesOrder' | 'soLine';
  type?: 'string' | 'number' | 'date' | 'boolean' | 'enum';
  filterable?: boolean;
}

/**
 * A tenant-defined Jobs-report view. Field references are registry keys
 * (Core fields or the tenant's own `fields` descriptors). The view marked
 * `isDefault` replaces Core's immutable "All Jobs" view — it is re-derived
 * from the pack on every load, so the dashboard's default columns are code,
 * not a one-time seed. Non-default views are seeded once by name; users may
 * edit or delete them afterwards.
 */
export interface TenantViewDef {
  name: string;
  /** Leading cascade list. Default 'jobs'. */
  list?: 'jobs' | 'parts' | 'salesOrders';
  /** Master-table columns (the default view's first column is always the job id field). */
  columns: string[];
  /** Detail-panel + export fields. */
  detailFields?: string[];
  filters?: { field: string; op: string; values: unknown[] }[];
  sorts?: { field: string; dir: 'asc' | 'desc' }[];
  /** Identity-ramp color key (e.g. 'slate-blue', 'clay', 'sage'). */
  color?: string;
  isDefault?: boolean;
}

export interface TenantUiPack {
  apiVersion: 1;
  terminology?: Partial<Lexicon>;
  fields?: TenantFieldDescriptor[];
  views?: TenantViewDef[];
  /** Core field keys this tenant hides everywhere. */
  hiddenFields?: string[];
  /** Explicit label overrides for Core registry fields (key → label). */
  fieldLabels?: Record<string, string>;
  /** ERP sync button; hidden when absent. `adapter` = a Core sync adapter run
   *  through /api/sync — the only sync path (the legacy tenant POST route was
   *  removed in Phase 4).
   *  Lightweight tier — relabels/repoints Core's default import window. For
   *  full control supply components.importWindow instead. */
  sync?: { label?: string; adapter: string };
  /** Push-back of scheduled dates to the ERP; hidden when absent. `pushback` = a Core
   *  push-back run through /api/push (spec §5.12) — the only push path, mirroring
   *  `sync.adapter` above. The push-back itself is declared by the client repo; Core
   *  owns the operator gate, the lock and the run record. */
  pushSchedule?: { label?: string; pushback: string };
  /** Live labour actuals feed for the daily production status module; hidden when absent. */
  actualsEndpoint?: string;
  /**
   * Tenant-supplied UI components (Vue components, passed by reference —
   * Core never imports tenant code). `importWindow` REPLACES the content of
   * the production scheduler's "Import Data" modal; contract: the component
   * emits 'close' (dismiss the modal) and 'synced' (server data changed —
   * Core reloads jobs/capacity/dataset). Typed `unknown` to keep this
   * module framework-agnostic data.
   */
  components?: { importWindow?: unknown };
}

export const DEFAULT_TERMS: Lexicon = {
  job:           { singular: 'Job',            plural: 'Jobs' },
  operation:     { singular: 'Operation',      plural: 'Operations' },
  resourceGroup: { singular: 'Resource Group', plural: 'Resource Groups' },
  salesOrder:    { singular: 'Sales Order',    plural: 'Sales Orders', short: 'Orders' },
  orderLine:     { singular: 'SO Line',        plural: 'SO Lines' },
  part:          { singular: 'Part',           plural: 'Parts' },
  customer:      { singular: 'Customer',       plural: 'Customers' },
};

let pack: TenantUiPack = { apiVersion: 1 };
let lexicon: Lexicon = { ...DEFAULT_TERMS };

export function setTenantUi(p: TenantUiPack | undefined): void {
  pack = p ?? { apiVersion: 1 };
  lexicon = { ...DEFAULT_TERMS };
  for (const [key, def] of Object.entries(pack.terminology ?? {})) {
    if (!def?.singular) continue;
    lexicon[key] = { plural: `${def.singular}s`, ...(lexicon[key] ?? {}), ...def };
  }
}

export function getTenantUi(): TenantUiPack {
  return pack;
}

/** The full resolved term: term('job') → { singular:'MO', plural:'MOs' } */
export function term(key: string): LexTerm {
  return lexicon[key] ?? { singular: key, plural: `${key}s` };
}

/** Display string: t('job') → 'Job' · t('job', 'plural') → 'Jobs' */
export function t(key: string, form: 'singular' | 'plural' | 'long' | 'short' = 'singular'): string {
  const trm = term(key);
  return trm[form] ?? trm.singular;
}

/** Resolve a dot path ('ext.manager') against an entity doc. */
export function resolvePath(entity: unknown, path: string): unknown {
  let cur: any = entity;
  for (const seg of path.split('.')) {
    if (cur == null) return null;
    cur = cur[seg];
  }
  return cur ?? null;
}
